import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Megaphone, CheckCircle2, XCircle, MinusCircle, RefreshCw, Send, AlertTriangle, Copy } from 'lucide-react';

const inputCls = "bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] h-11 rounded-xl";
const selectContentCls = "bg-[#0A1F1A] border-white/10 text-white";

const STATUS_STYLE = {
  sent: { icon: CheckCircle2, cls: 'text-[#6BCB77]', label: 'Enviado' },
  error: { icon: XCircle, cls: 'text-red-400', label: 'Error' },
  skipped: { icon: MinusCircle, cls: 'text-white/30', label: 'Omitido' },
};

export default function MetaCRM() {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('all');
  const [testCode, setTestCode] = useState('');
  const [testStage, setTestStage] = useState('calificado');
  const [testLeadgen, setTestLeadgen] = useState('');
  const [busy, setBusy] = useState(false);
  const [adPerf, setAdPerf] = useState(null);
  const [perfDays, setPerfDays] = useState('90');

  useEffect(() => {
    api.get(`/meta/ad-performance?days=${perfDays}`).then(setAdPerf).catch(() => {});
  }, [perfDays]);

  const load = useCallback(async () => {
    try {
      const [s, e] = await Promise.all([
        api.get('/meta/status'),
        api.get(`/meta/events?limit=100${filter !== 'all' ? `&status=${filter}` : ''}`),
      ]);
      setStatus(s);
      setEvents(e || []);
      setTestCode(prev => prev || (s.config?.test_event_code || ''));
    } catch {
      toast.error('No se pudo cargar el estado de Meta');
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async (patch) => {
    try {
      const cfg = await api.put('/meta/config', patch);
      setStatus(s => ({ ...s, config: cfg }));
      toast.success('Configuracion guardada');
    } catch { toast.error('No se pudo guardar'); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await api.post('/meta/test', { test_event_code: testCode, stage: testStage, meta_leadgen_id: testLeadgen });
      if (r.status === 'sent') toast.success(`Evento "${r.event_name}" enviado a Meta (${r.mode.toUpperCase()})`);
      else toast.error(r.detail || 'Meta rechazo el evento');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error enviando prueba'); }
    setBusy(false);
  };

  const retry = async (id) => {
    try {
      const r = await api.post(`/meta/events/${id}/retry`);
      if (r.status === 'sent') toast.success('Reenviado correctamente');
      else toast.error(r.detail || 'Sigue fallando');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo reenviar'); }
  };

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success('Copiado');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = txt;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); toast.success('Copiado'); } catch { toast.error('Copia manualmente el texto'); }
      document.body.removeChild(ta);
    }
  };

  if (!status) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-[#A38560] border-t-transparent rounded-full" /></div>;

  const cfg = status.config || {};
  const webhookUrl = `${window.location.origin}/api/webhook`;
  const siteSnippet = `<!-- SELECTO · Pixel de Meta + captura de atribucion (pegar en el <head> de TODO el sitio) -->
<script>
!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
document,'script','https://connect.facebook.net/en_US/fbevents.js');
fbq('init','${status.pixel_id || 'TU_PIXEL_ID'}');
fbq('track','PageView');
(function(){try{
  var qp=new URLSearchParams(location.search);
  var keys=['fbclid','utm_source','utm_medium','utm_campaign','utm_content','utm_term','ad_id','adset_id','campaign_id'];
  var store={};try{store=JSON.parse(localStorage.getItem('selecto_attr')||'{}')}catch(e){}
  var changed=false;
  keys.forEach(function(k){var v=qp.get(k);if(v&&store[k]!==v){store[k]=v;changed=true}});
  if(changed){
    store.saved_at=Date.now();store.landing_url=location.href;
    localStorage.setItem('selecto_attr',JSON.stringify(store));
    document.cookie='selecto_attr='+encodeURIComponent(JSON.stringify(store))+';path=/;max-age=7776000';
  }
}catch(e){}})();
</script>`;

  const Pill = ({ ok, children }) => (
    <span className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${ok ? 'bg-[#6BCB77]/10 text-[#6BCB77] border-[#6BCB77]/30' : 'bg-red-500/10 text-red-300 border-red-500/30'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-[#6BCB77]' : 'bg-red-400'}`} />{children}
    </span>
  );

  return (
    <div data-testid="meta-crm-page" className="space-y-6">
      <h1 className="font-outfit text-2xl lg:text-3xl font-medium tracking-tight text-white flex items-center gap-3">
        <Megaphone size={24} strokeWidth={1.5} className="text-[#A38560]" /> Meta Ads · Feedback de Leads
      </h1>

      {/* Connection status */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-4">
        <h2 className="font-outfit text-lg font-medium text-white">Estado de la conexion</h2>
        <div className="flex flex-wrap gap-2" data-testid="meta-status-pills">
          <Pill ok={status.crm_dataset_configured}>Dataset CRM {status.crm_dataset_configured ? `···${status.crm_dataset_id}` : 'sin configurar'}</Pill>
          <Pill ok={status.pixel_configured}>Pixel web {status.pixel_id ? `···${String(status.pixel_id).slice(-4)}` : 'sin configurar'}</Pill>
          <Pill ok={status.page_token_configured}>Token de Pagina (webhook)</Pill>
          <Pill ok={cfg.enabled !== false}>{cfg.enabled !== false ? 'Envio activo' : 'Envio pausado'}</Pill>
        </div>
        {!status.crm_dataset_configured && (
          <div className="flex gap-2 p-3 bg-amber-500/10 border border-amber-500/25 rounded-xl">
            <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-200/90 leading-relaxed">
              Falta el <b>Dataset de CRM</b>. Sin el, los eventos se envian al Pixel web y Meta no podra activar
              la optimizacion por <b>Leads de conversion</b>. Crealo en Administrador de Eventos → Conectar origenes
              de datos → CRM y pasa el Dataset ID + token para configurarlo.
            </p>
          </div>
        )}
        <div className="grid grid-cols-3 gap-3">
          {['sent', 'error', 'skipped'].map(k => (
            <div key={k} className="bg-[#16302B] rounded-xl border border-white/5 p-3 text-center">
              <p className={`text-2xl font-outfit tabular-nums ${STATUS_STYLE[k].cls}`}>{status.counts_7d?.[k] || 0}</p>
              <p className="text-[10px] uppercase tracking-wider text-white/40 mt-1">{STATUS_STYLE[k].label} · 7d</p>
            </div>
          ))}
        </div>
        <div className="space-y-2 text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white/40">Webhook:</span>
            <span className="font-mono text-[#A38560] break-all">{webhookUrl}</span>
            <button onClick={() => copy(webhookUrl)} className="text-white/30 hover:text-white"><Copy size={12} /></button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-white/40">Verify token:</span>
            <span className="font-mono text-white/60">{status.verify_token}</span>
            <button onClick={() => copy(status.verify_token)} className="text-white/30 hover:text-white"><Copy size={12} /></button>
          </div>
          <p className="text-white/30">API {status.api_version} · lead_event_source: {status.lead_event_source}</p>
        </div>
      </div>

      {/* Site snippet for Webflow */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="font-outfit text-lg font-medium text-white">Codigo para somosselecto.com (Webflow)</h2>
          <button onClick={() => copy(siteSnippet)} data-testid="copy-site-snippet"
            className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-xl bg-[#A38560]/15 text-[#A38560] border border-[#A38560]/30">
            <Copy size={12} /> Copiar codigo
          </button>
        </div>
        <p className="text-xs text-white/40 leading-relaxed">
          Pegalo en <b>Webflow → Site settings → Custom code → Head code</b> y publica. Instala el Pixel y guarda por
          90 dias el <span className="font-mono text-white/60">fbclid</span> y la campana de origen, para que un visitante
          que llega hoy por un anuncio y llena el formulario dias despues igual quede atribuido a esa pauta.
        </p>
        <pre className="bg-[#03110D] rounded-xl p-3 text-[10px] leading-relaxed text-white/50 overflow-x-auto max-h-56 whitespace-pre">{siteSnippet}</pre>
      </div>

      {/* Config */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-4">
        <h2 className="font-outfit text-lg font-medium text-white">Ajustes de envio</h2>
        <div className="flex items-center justify-between bg-[#16302B] rounded-xl border border-white/5 p-3">
          <div>
            <p className="text-sm text-white">Enviar eventos a Meta</p>
            <p className="text-xs text-white/40">Reporta cada cambio de etapa del lead (incluye descalificados)</p>
          </div>
          <Switch checked={cfg.enabled !== false} onCheckedChange={v => saveConfig({ enabled: v })} data-testid="meta-enabled-switch" />
        </div>
        <div className="flex items-center justify-between bg-[#16302B] rounded-xl border border-white/5 p-3">
          <div>
            <p className="text-sm text-white">Incluir leads de formularios web</p>
            <p className="text-xs text-white/40">Reporta tambien los leads de landing/web (se identifican con fbc/fbp o correo)</p>
          </div>
          <Switch checked={cfg.send_web_events !== false} onCheckedChange={v => saveConfig({ send_web_events: v })} data-testid="meta-web-switch" />
        </div>
        <div className="bg-[#16302B] rounded-xl border border-white/5 p-3 space-y-2">
          <Label className="text-white/50 text-xs">Codigo de prueba (test_event_code)</Label>
          <div className="flex gap-2">
            <Input value={testCode} onChange={e => setTestCode(e.target.value)} placeholder="TEST12345" className={inputCls} data-testid="meta-test-code" />
            <button onClick={() => saveConfig({ test_event_code: testCode })}
              className="px-4 rounded-xl bg-[#A38560]/20 text-[#A38560] border border-[#A38560]/30 text-sm whitespace-nowrap">Guardar</button>
          </div>
          <p className="text-[11px] text-white/30">Solo para validar en "Eventos de prueba" del Administrador de Eventos. Dejalo vacio en produccion.</p>
        </div>
      </div>

      {/* Stage mapping */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-3">
        <h2 className="font-outfit text-lg font-medium text-white">Mapeo de etapas → Meta</h2>
        <div className="space-y-1.5" data-testid="meta-stage-map">
          {(status.stage_map || []).map(m => (
            <div key={m.stage} className="flex items-center gap-2 bg-[#16302B] rounded-xl border border-white/5 px-3 py-2 text-xs">
              <span className="text-white/70 w-32 shrink-0">{m.label}</span>
              <span className="text-white/20">→</span>
              <span className={`font-medium ${m.negative ? 'text-red-300' : 'text-[#6BCB77]'}`}>{m.crm_event || '—'}</span>
              {m.negative && <span className="text-[9px] uppercase tracking-wider text-red-400/70 border border-red-500/30 rounded-full px-1.5 py-0.5">Feedback negativo</span>}
              {!status.crm_dataset_configured && <span className="ml-auto text-white/30">web: {m.web_event || '—'}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Test event */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-3">
        <h2 className="font-outfit text-lg font-medium text-white">Enviar evento de prueba</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div>
            <Label className="text-white/50 text-xs mb-1 block">Etapa</Label>
            <Select value={testStage} onValueChange={setTestStage}>
              <SelectTrigger className={inputCls} data-testid="meta-test-stage"><SelectValue /></SelectTrigger>
              <SelectContent className={selectContentCls}>
                {(status.stage_map || []).map(m => <SelectItem key={m.stage} value={m.stage}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-white/50 text-xs mb-1 block">Leadgen ID de Meta (opcional, para probar modo CRM)</Label>
            <Input value={testLeadgen} onChange={e => setTestLeadgen(e.target.value)} placeholder="1234567890123456" className={inputCls} data-testid="meta-test-leadgen" />
          </div>
        </div>
        <button onClick={sendTest} disabled={busy} data-testid="meta-send-test"
          className="flex items-center gap-2 bg-[#A38560] text-[#03110D] font-medium px-4 py-2 rounded-xl text-sm disabled:opacity-50">
          <Send size={14} /> {busy ? 'Enviando...' : 'Enviar prueba'}
        </button>
      </div>

      {/* Events log */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="font-outfit text-lg font-medium text-white">Historial de eventos</h2>
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="bg-[#16302B] border-white/10 text-white h-9 rounded-xl text-xs w-36" data-testid="meta-filter"><SelectValue /></SelectTrigger>
              <SelectContent className={selectContentCls}>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="sent">Enviados</SelectItem>
                <SelectItem value="error">Con error</SelectItem>
                <SelectItem value="skipped">Omitidos</SelectItem>
              </SelectContent>
            </Select>
            <button onClick={load} className="text-white/40 hover:text-white p-2" data-testid="meta-refresh"><RefreshCw size={14} /></button>
          </div>
        </div>
        <div className="space-y-1.5" data-testid="meta-events-list">
          {events.map(ev => {
            const st = STATUS_STYLE[ev.status] || STATUS_STYLE.skipped;
            const Icon = st.icon;
            return (
              <div key={ev.id} data-testid={`meta-event-${ev.id}`} className="flex items-start gap-3 bg-[#16302B] rounded-xl border border-white/5 p-3">
                <Icon size={15} className={`${st.cls} shrink-0 mt-0.5`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white truncate">
                    {ev.event_name || ev.stage} <span className="text-white/40">· {ev.lead_name || ev.lead_id}</span>
                  </p>
                  <p className="text-[11px] text-white/40 break-words">{ev.detail}</p>
                  <p className="text-[10px] text-white/25 mt-0.5">
                    {new Date(ev.created_at).toLocaleString('es-CO')} · modo {ev.mode || '—'} · {ev.triggered_by}
                    {ev.test_mode ? ' · PRUEBA' : ''}
                  </p>
                </div>
                {ev.status === 'error' && (
                  <button onClick={() => retry(ev.id)} data-testid={`meta-retry-${ev.id}`}
                    className="text-[11px] px-2.5 py-1 rounded-lg bg-[#A38560]/15 text-[#A38560] border border-[#A38560]/30 shrink-0">Reintentar</button>
                )}
              </div>
            );
          })}
          {events.length === 0 && <p className="text-sm text-white/30 text-center py-6">Aun no hay eventos registrados</p>}
        </div>
      </div>
      {/* Ad performance */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="font-outfit text-lg font-medium text-white">Rendimiento por anuncio / campana</h2>
          <Select value={perfDays} onValueChange={setPerfDays}>
            <SelectTrigger className="bg-[#16302B] border-white/10 text-white h-9 rounded-xl text-xs w-36" data-testid="perf-days"><SelectValue /></SelectTrigger>
            <SelectContent className={selectContentCls}>
              <SelectItem value="30">Ultimos 30 dias</SelectItem>
              <SelectItem value="90">Ultimos 90 dias</SelectItem>
              <SelectItem value="365">Ultimo ano</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="ad-performance-table">
            <thead>
              <tr className="text-white/35 text-[10px] uppercase tracking-wider">
                <th className="text-left font-normal pb-2">Origen</th>
                <th className="text-left font-normal pb-2">Tipo</th>
                <th className="text-right font-normal pb-2">Leads</th>
                <th className="text-right font-normal pb-2">Calificados</th>
                <th className="text-right font-normal pb-2">Cerrados</th>
                <th className="text-right font-normal pb-2">Descalificados</th>
                <th className="text-right font-normal pb-2">Identificables en Meta</th>
              </tr>
            </thead>
            <tbody>
              {(adPerf?.rows || []).map(r => (
                <tr key={r.attribution + r.origin} data-testid={`perf-row-${r.origin}`} className="border-t border-white/5">
                  <td className="py-2 pr-3 text-white/80 max-w-[280px] truncate">{r.origin}</td>
                  <td className="py-2 pr-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${r.attribution === 'pauta' ? 'bg-[#6BCB77]/10 text-[#6BCB77] border-[#6BCB77]/25' : 'bg-amber-500/10 text-amber-300 border-amber-500/25'}`}>
                      {r.attribution === 'pauta' ? 'Pauta' : 'Organico'}
                    </span>
                  </td>
                  <td className="py-2 text-right tabular-nums text-white/60">{r.total}</td>
                  <td className="py-2 text-right tabular-nums text-[#6BCB77]">{r.qualified} <span className="text-white/25">({r.qualified_pct}%)</span></td>
                  <td className="py-2 text-right tabular-nums text-[#A38560]">{r.closed}</td>
                  <td className="py-2 text-right tabular-nums text-red-300">{r.lost} <span className="text-white/25">({r.lost_pct}%)</span></td>
                  <td className={`py-2 text-right tabular-nums ${r.match_pct >= 50 ? 'text-white/50' : 'text-amber-400/80'}`}>{r.match_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!adPerf?.rows || adPerf.rows.length === 0) && <p className="text-sm text-white/30 text-center py-6">Sin datos en el periodo</p>}
        </div>
        <p className="text-[11px] text-white/30">
          "Identificables en Meta" = leads que llegaron con lead_id de Meta o con fbclid/fbc. Si ese porcentaje es bajo en
          una campana de landing, revisa que el codigo del sitio este instalado y que el anuncio no borre el fbclid.
        </p>
      </div>
    </div>
  );
}
